<script lang="ts">
export type OnResize = (values: { from: string; to: string }) => Promise<void>
// 親がドラッグ中のバーを追うための口。開始と終了は必ず対で要るのでまとめて受け取る
// NOTE: dataTransferはdragover中に読めないため、掴んだバーと掴んだ位置は開始時に渡しておく
export type DragTracking = {
  onStart: (values: { id: string; offsetX: number }) => void
  onEnd: () => void
}
</script>

<script lang="ts" setup>
import { useBoolean } from '@/src/hooks/useBoolean'
import { useResizingScheduleBar } from '@/src/hooks/useResizingScheduleBar'
import { useScheduleBarStyle } from '@/src/hooks/useScheduleBarStyle'
import { useTemporalPlainTime } from '@/src/hooks/useTemporalPlainTime'
import { useTouchDevice } from '@/src/hooks/useTouchDevice'
import { TIME_LINE_HOUR_WIDTH } from '@/src/lib/constants'

const props = defineProps<{
  id: string
  title: string
  companyLargeCategoryName: string
  colorHue: number
  from: string
  to: string
  burst?: boolean
  note: string
  isOverlap?: boolean
  isNew?: boolean
  isSelectionRange?: boolean
  selected?: boolean
  taskTemplateId?: string | undefined
  draggable?: boolean
  onClick?: () => void
  onResize?: OnResize
  dragTracking?: DragTracking
  // ドロップ前に移動先を見せるための一時的なずらし量
  previewShiftMinutes?: number
  lane?: number
  totalLanes?: number
}>()

const [isActive, setIsActive] = useBoolean(false)

const hasNote = computed(() => props.note.length > 0)

// SEE: https://developer.mozilla.org/ja/docs/Web/HTML/Global_attributes/draggable
const draggableValue = computed(() => (props.draggable ? 'true' : 'false'))

const classNames = computed(() => {
  return {
    'schedule-bar--burst': props.burst,
    'schedule-bar--warning': props.isOverlap || (!props.isNew && !props.taskTemplateId),
    'schedule-bar--active': isActive.value,
    'schedule-bar--disabled': props.onClick === undefined,
    'schedule-bar--new': props.isNew,
    'schedule-bar--selection-range': props.isSelectionRange,
    'schedule-bar--selected': props.selected,
  }
})

const from = useTemporalPlainTime(toRef(props, 'from'))
const to = useTemporalPlainTime(toRef(props, 'to'))
const lane = toRef(props, 'lane')
const totalLanes = toRef(props, 'totalLanes')

// NOTE: 移動と同じくタッチデバイスでは掴めない幅なので、ハンドルがバー本体のタップを奪わないよう出さない
const isTouchDevice = useTouchDevice()
const resizable = computed(() => !!props.draggable && !!props.onResize && !isTouchDevice.value)
const { resizing, previewFrom, previewTo, startResize, justResized } = useResizingScheduleBar({
  from,
  to,
  enabled: resizable,
  onEnd: async (values) => {
    await props.onResize?.({
      from: values.from.toString({ smallestUnit: 'minute' }),
      to: values.to.toString({ smallestUnit: 'minute' }),
    })
  },
})

const baseStyle = useScheduleBarStyle({ from: previewFrom, to: previewTo, lane, totalLanes })

const previewShiftPx = computed(() => ((props.previewShiftMinutes ?? 0) / 60) * TIME_LINE_HOUR_WIDTH)

const barStyle = computed(() => {
  return {
    ...baseStyle.value,
    background: `linear-gradient(-220deg, hsl(${props.colorHue}, 97%, 98%), hsl(${props.colorHue}, 96%, 96%))`,
    transform: previewShiftPx.value === 0 ? undefined : `translateX(${previewShiftPx.value}px)`,
  }
})

const handleDragstart = (event: DragEvent): void => {
  // NOTE: リサイズ操作をバー移動のドラッグとして開始させない
  if (resizing.value) {
    event.preventDefault()
    return
  }

  const { dataTransfer, offsetX } = event
  if (!dataTransfer) return

  dataTransfer.effectAllowed = 'move'
  // NOTE: 中身は読まないが、Firefoxはこれを呼ばないとドラッグが始まらない
  dataTransfer.setData('text/plain', props.id)
  setIsActive.on()
  props.dragTracking?.onStart({ id: props.id, offsetX })
}

const handleDragend = (): void => {
  setIsActive.off()
  props.dragTracking?.onEnd()
}

const handleClick = (): void => {
  if (justResized.value) return

  props.onClick && props.onClick()
}
</script>

<template>
  <!-- NOTE: @mousedown.stopは予定登録UIのマウス選択動作とバッティングしないように -->
  <div
    class="schedule-bar"
    :class="classNames"
    :style="barStyle"
    :draggable="draggableValue"
    :data-test-selector="`schedule-bar-${id}`"
    @click="handleClick"
    @dragstart="handleDragstart"
    @dragend="handleDragend"
    @mousedown.stop
  >
    <span v-if="selected" class="schedule-bar__check" data-test-selector="schedule-bar-selected">
      <span class="tw:iconify tw:bi--check-lg" />
    </span>
    <div class="schedule-bar__name">
      <small v-if="hasNote" class="schedule-bar__sub-title" v-text="note" />
      <small v-if="taskTemplateId" class="schedule-bar__sub-title" v-text="companyLargeCategoryName" />
      <span v-if="burst" class="hstack tw:gap-1">
        <span class="tw:iconify tw:bi--exclamation-circle" />
        突発
      </span>
      <span v-if="isOverlap" class="has-text-danger hstack tw:gap-1">
        <span class="tw:iconify tw:bi--exclamation-circle" />
        重複
      </span>
      <span v-text="title" />
    </div>
    <!-- NOTE: @mousedown.preventはテキスト選択とバー移動の抑止 -->
    <template v-if="resizable">
      <div
        class="schedule-bar__resize-handle schedule-bar__resize-handle--start"
        data-test-selector="schedule-bar-resize-start"
        @mousedown.prevent="startResize('start', $event)"
      />
      <div
        class="schedule-bar__resize-handle schedule-bar__resize-handle--end"
        data-test-selector="schedule-bar-resize-end"
        @mousedown.prevent="startResize('end', $event)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
@use '@/stylesheets/application/resources';

.schedule-bar {
  border-color: #cacaca;
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  color: resources.$grey-darker;
  cursor: pointer;

  // NOTE: 高さはuseScheduleBarStyleで動的に計算される
  position: absolute;

  &:hover {
    opacity: 0.8;
  }

  &.schedule-bar--warning {
    border-color: #d65967 !important;
  }

  &.schedule-bar--burst {
    // NOTE: スタイル属性上書きの為にbackground-colorだと駄目
    background: #d65967 !important;
    border-color: #d65967 !important;
    color: resources.$white !important;
  }

  &.schedule-bar--active {
    // FIXME: 効く場合と効かない場合があるが原因不明、、効かない場合もschedule-bar--activeクラスは付いている。
    cursor: move;
  }

  &.schedule-bar--disabled {
    @include resources.chat-disabled;
  }

  &.schedule-bar--new {
    cursor: move;
    opacity: 1;
    user-select: none;
  }

  // NOTE: 15分の実績は幅25pxしかなく枠の内側では示せないため、外側のリングで選択を表す
  &.schedule-bar--selected {
    box-shadow: 0 0 0 2px resources.$primary;
  }

  // NOTE: どの実績にかかっているかを見せたいので塗らない(背景はbarStyleのインライン指定なので!importantが要る)
  &.schedule-bar--selection-range {
    background: transparent !important;
    border-color: resources.$primary;
    border-style: dashed;
    border-width: 2px;
  }
}

.schedule-bar__check {
  align-items: center;
  background-color: resources.$primary;
  border-radius: 50%;
  color: resources.$white;
  display: flex;
  font-size: 10px;
  height: 14px;
  justify-content: center;
  position: absolute;
  right: 2px;
  top: 2px;
  width: 14px;
  z-index: 2;
}

.schedule-bar__name {
  font-size: 12px;
  font-weight: 500;
  height: calc(100% - 8px);
  line-height: 1.2;
  margin: 4px;
  overflow: hidden;
  position: relative;
  white-space: nowrap;
}

.schedule-bar__sub-title {
  display: block;
  opacity: 0.8;
  white-space: nowrap;
}

.schedule-bar__resize-handle {
  bottom: 0;
  cursor: ew-resize;
  position: absolute;
  top: 0;

  // NOTE: 最小幅(15分 = 25px)の予定でも本体をクリックできる幅に留める
  width: 8px;
  z-index: 1;

  // NOTE: ホバー時のみ掴める位置を示す
  &::after {
    background-color: currentcolor;
    border-radius: 1px;
    bottom: 25%;
    content: '';
    left: 3px;
    opacity: 0;
    position: absolute;
    top: 25%;
    transition: opacity 0.15s;
    width: 2px;
  }

  .schedule-bar:hover &::after {
    opacity: 0.4;
  }
}

.schedule-bar__resize-handle--start {
  left: 0;
}

.schedule-bar__resize-handle--end {
  right: 0;
}
</style>
